/**
 * useSiteSlug — the single source of truth for "which brand am I on?"
 *
 * Reads two signals (in resolution order):
 *   1. `?site=<slug>`      — preview-mode override (works on any host)
 *   2. hostname alias      — socialcapital.cc → "socialcapital", etc.
 *
 * Reactive to react-router location so the value RECOMPUTES on SPA
 * navigation (fixes iter123 bug where App.js used a bare
 * window.location.search read inside an IIFE and never updated after
 * the first render).
 *
 * Returns the resolved slug in lower-case, or "socialpulse" as the
 * default. All brand-aware components (App.js chrome suppression,
 * BrandDocumentHead, HomeRouter, sc-brand-bar links) should use this
 * hook rather than calling resolveSiteSlug directly.
 */
import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { resolveSiteSlug } from "@/config/siteConfig";

export default function useSiteSlug() {
  const { search } = useLocation();
  return useMemo(() => {
    try {
      const params = new URLSearchParams(search);
      const q = (params.get("site") || "").toLowerCase();
      return (resolveSiteSlug({ queryParam: q }) || "socialpulse").toLowerCase();
    } catch {
      return "socialpulse";
    }
  }, [search]);
}

/** Preserve the current brand's ?site param on outgoing hrefs. */
export function withBrandSearch(pathname, search) {
  try {
    const p = new URLSearchParams(search);
    const site = p.get("site");
    if (!site) return pathname;
    const [path, existing = ""] = pathname.split("?");
    const merged = new URLSearchParams(existing);
    if (!merged.get("site")) merged.set("site", site);
    return `${path}?${merged.toString()}`;
  } catch {
    return pathname;
  }
}
